function isCompactViewport() {
    return window.matchMedia("(max-width: 720px)").matches;
}

function getGap() {
    return isCompactViewport() ? 8 : 10;
}

function getColumns(containerWidth, gap) {
    if (isCompactViewport()) {
        return 2;
    }

    return Math.max(2, Math.floor((containerWidth + gap) / (200 + gap)));
}

function getHomeExcerptLength(totalLength) {
    if (totalLength <= 300) {
        return totalLength;
    }

    if (totalLength >= 5000) {
        return 1500;
    }

    const decayRate = 0.0005405678155298629;
    const denominator = 1 - Math.exp(-decayRate * 4700);
    const displayLength =
        300 +
        (1200 * (1 - Math.exp(-decayRate * (totalLength - 300)))) /
            denominator;

    return Math.min(totalLength, Math.round(displayLength));
}

function populateHomeExcerpt(card) {
    if (!card.classList.contains("gh-card-home")) {
        return;
    }

    const excerpt = card.querySelector(".gh-card-excerpt");
    const source = card.querySelector(".gh-card-source");

    if (!excerpt || !source || excerpt.dataset.populated === "true") {
        return;
    }

    // Never insert the template's rich HTML into the live document. Even hidden
    // iframes/scripts can start third-party players and crash Safari's page.
    const content = source.content;
    if (!content) {
        return;
    }

    content.querySelectorAll("script, style").forEach((element) => element.remove());
    const normalizedText = (content.textContent || "")
        .replace(/\r\n|\r|\n/g, "")
        .replace(/\s{2,}/g, " ")
        .trim();
    const textCharacters = Array.from(normalizedText);
    const displayLength = getHomeExcerptLength(textCharacters.length);
    const truncatedText =
        displayLength < textCharacters.length
            ? `${textCharacters.slice(0, displayLength).join("")}...`
            : normalizedText;

    excerpt.textContent = truncatedText;
    excerpt.dataset.populated = "true";
    source.remove();
}

export default function homeLayout() {
    const feeds = Array.from(
        document.querySelectorAll(".gh-feed-page .gh-postfeed"),
    );

    if (!feeds.length) {
        return null;
    }

    let allowAnimatedLayout = false;

    const requestLayouts = feeds.map((feed) => {
        let frameRequested = false;
        let hasLaidOut = false;
        let shouldAnimateNextLayout = false;
        const layoutRoot = feed.parentElement;

        feed.style.visibility = "hidden";

        function layout() {
            const cards = Array.from(feed.querySelectorAll(".gh-card"));
            const containerWidth = Math.floor(feed.clientWidth);

            if (!cards.length || !containerWidth) {
                return;
            }

            if (shouldAnimateNextLayout && hasLaidOut && allowAnimatedLayout) {
                feed.classList.add("is-animated");
            } else {
                feed.classList.remove("is-animated");
            }

            cards.forEach((card) => {
                populateHomeExcerpt(card);
            });

            const gap = getGap();
            const columns = getColumns(containerWidth, gap);
            const itemWidth = Math.max(
                0,
                Math.floor((containerWidth - gap * (columns - 1)) / columns),
            );
            const columnHeights = new Array(columns).fill(0);

            feed.classList.add("is-enhanced");

            cards.forEach((card) => {
                card.style.width = `${itemWidth}px`;
                card.style.height = "auto";
                card.style.transform = "translate(0, 0)";
            });

            cards.forEach((card) => {
                const height = Math.ceil(card.offsetHeight);
                let targetColumn = 0;
                let targetY = columnHeights[0];

                for (let index = 1; index < columns; index += 1) {
                    if (columnHeights[index] < targetY) {
                        targetColumn = index;
                        targetY = columnHeights[index];
                    }
                }

                const x = targetColumn * (itemWidth + gap);

                card.style.height = `${height}px`;
                card.style.transform = `translate(${x}px, ${targetY}px)`;
                columnHeights[targetColumn] = targetY + height + gap;
            });

            feed.style.height = `${Math.max(...columnHeights) - gap}px`;
            feed.style.visibility = "";
            hasLaidOut = true;
            shouldAnimateNextLayout = false;
        }

        function requestLayout(animate = false) {
            if (frameRequested) {
                shouldAnimateNextLayout = shouldAnimateNextLayout || animate;
                return;
            }

            frameRequested = true;
            shouldAnimateNextLayout = animate;

            requestAnimationFrame(() => {
                frameRequested = false;
                layout();
            });
        }

        if (window.ResizeObserver) {
            const resizeObserver = new ResizeObserver(() => {
                requestLayout(false);
            });

            if (layoutRoot) {
                resizeObserver.observe(layoutRoot);
            }
        }

        if (window.MutationObserver) {
            const mutationObserver = new MutationObserver(() => {
                requestLayout(false);
            });

            mutationObserver.observe(feed, {
                childList: true,
            });
        }

        return requestLayout;
    });

    function boot() {
        requestLayouts.forEach((requestLayout) => {
            requestLayout(false);
        });

        if (document.fonts) {
            document.fonts.ready.then(() => {
                requestLayouts.forEach((requestLayout) => {
                    requestLayout(false);
                });
            });
        }

        window.addEventListener(
            "resize",
            () => {
                allowAnimatedLayout = true;
                requestLayouts.forEach((requestLayout) => {
                    requestLayout(true);
                });
            },
            {
                passive: true,
            },
        );
    }

    boot();

    return requestLayouts;
}
